import { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import useField from 'hooks/useField';
import { colorsButton, shadowLight } from 'app/colors';
import { borderRadii, gutters } from 'app/spacing';
import { linkCss } from 'app/typography';
import BaseSelect from 'components/Inputs/Select';
import Icon, { IconSizing } from 'components/Kizen/Icon';
import IconButton from 'components/Kizen/IconButton';
import Handle from './Handle';
import { useTooltip, truncationPredicates } from 'components/Kizen/Tooltip';
import {
  getExecutionMode,
  getHistoryId,
  getPlacingExitLoopSteps,
  getStepMovingBlocker,
} from 'pages/AutomationEngine/store/selectors';
import { useAutomationsSelector } from 'pages/AutomationEngine/store/react';
import { AUTOMATION_STEPS } from 'pages/AutomationEngine/steps';
import { MAX_LOOP_DEPTH } from 'pages/AutomationEngine/constants';
import { canPlaceExitLoopAt } from 'pages/AutomationEngine/store/ts-utilities';

import { ADD_BUTTON_HEIGHT, ADD_BUTTON_WIDTH } from './addButtonConstants';

const overflowXOrY = (el) =>
  truncationPredicates.x(el) || truncationPredicates.y(el);

// The "Place Here" button is naturally just shy of 90px anyways, so this works out well.
const PLACEMENT_BUTTON_WIDTH = 90;
const MENU_OPTION_HEIGHT = 32;
const MIN_VISIBLE_OPTIONS = 5;

// At times we're going to replace the OverlayButton with PlacementButton,
// and Flow layout works out nicer if they are the same height, so they don't
// need any repositioning.
const ConsistentHeightWrapper = styled.div`
  display: flex;
  align-items: center;
  height: ${ADD_BUTTON_HEIGHT}px;
`;

const PlacementWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${({ centered }) => (centered ? 'center' : 'flex-start')};
  width: ${ADD_BUTTON_WIDTH}px;
  height: ${ADD_BUTTON_HEIGHT}px;
`;

const PlacementButton = styled.button`
  position: relative;
  flex-shrink: 0;
  height: ${ADD_BUTTON_HEIGHT}px;
  width: ${PLACEMENT_BUTTON_WIDTH}px;
  text-overflow: ellipsis; // ensure truncation when translated
  overflow: hidden;
  white-space: nowrap;

  padding: 1px ${gutters.spacing(2)}px 0;
  ${linkCss()}
  background: var(--color-background-white);
  border-radius: ${borderRadii.small};
  &,
  &:focus {
    outline: none;
  }
  border: 1px dashed var(--color-button-secondary-default);
  ${({ dropping }) =>
    dropping &&
    css`
      border-style: solid;
    `}
`;

const StyledPlusButton = styled(IconButton)`
  border: 1px solid var(--color-border-medium-light);
  box-shadow: unset;
  &&:active > :first-child {
    color: var(--color-button-secondary-pressed);
  }
`;

const StyledMenu = styled.div`
  position: absolute;
`;

const Select = styled(BaseSelect)`
  ${shadowLight}
  && {
    width: auto;
  }
`;

const DropArea = styled.span`
  position: absolute;
  top: -${gutters.spacing(5)}px;
  bottom: -${gutters.spacing(5)}px;
  right: -${gutters.spacing(11)}px;
  left: -${gutters.spacing(11)}px;
`;

export default function AddButton(props) {
  const { t } = useTranslation();
  const [{ onBlur: onBlurTooltip, ...tooltipProps }, tooltip] = useTooltip(
    {},
    overflowXOrY
  );
  const {
    id,
    placing,
    centered,
    onClick,
    onDrop,
    showMenu: showMenuProp,
    options,
    onSelectOption,
    disableRootAdds,
    loopDepth,
    loopVariables,
    lastPlacingStep,
    ...others
  } = props;

  const historyId = useAutomationsSelector(getHistoryId);
  const executionMode = useAutomationsSelector(getExecutionMode);
  const stepMovingBlocker = useAutomationsSelector(getStepMovingBlocker);
  const placingExitLoopSteps = useAutomationsSelector(getPlacingExitLoopSteps);

  // an Exit Loop - moved alone or inside a goal/condition - can only land
  // inside the loop it exits, or inside any loop when it no longer knows which
  // that is
  const outsideExitLoopTarget = placingExitLoopSteps.some(
    (exitLoopStep) => !canPlaceExitLoopAt(exitLoopStep, loopVariables)
  );

  const disableDropInLoop =
    placing &&
    ((loopDepth === MAX_LOOP_DEPTH &&
      lastPlacingStep?.type === AUTOMATION_STEPS.loop.type) ||
      outsideExitLoopTarget ||
      (loopDepth !== 0 && stepMovingBlocker));

  const disableFeatures = historyId || executionMode || disableRootAdds;
  const propsRef = useRef();
  propsRef.current = props;

  const buttonRef = useRef();
  const menuRef = useRef();
  const containerRef = useRef(null);

  const [showMenu, setShowMenu] = useField(
    (prev) => {
      if (Boolean(showMenuProp) === showMenuProp) {
        // Is boolean
        return showMenuProp;
      }
      if (prev === undefined) {
        return false;
      }
      return prev;
    },
    [showMenuProp]
  );
  const [menuPosition, setMenuPosition] = useState(null);
  const requiredMenuHeight =
    Math.min(options?.length ?? 0, MIN_VISIBLE_OPTIONS) * MENU_OPTION_HEIGHT;

  useLayoutEffect(() => {
    if (showMenu) {
      let frameId;
      let lastRect;
      let lastContainerRect;
      const button = buttonRef.current;
      containerRef.current = document.querySelector('.FlowContainer');

      const syncPosition = () => {
        const rect = button.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        if (
          !lastRect ||
          !lastContainerRect ||
          lastRect.top !== rect.top ||
          lastRect.right !== rect.right ||
          lastContainerRect.top !== containerRect.top ||
          lastContainerRect.right !== containerRect.right ||
          lastContainerRect.bottom !== containerRect.bottom ||
          lastContainerRect.left !== containerRect.left
        ) {
          lastRect = rect;
          lastContainerRect = containerRect;
          const spaceBelow = window.innerHeight - rect.bottom;
          const openAbove = spaceBelow < requiredMenuHeight;
          setMenuPosition({
            top: openAbove ? 'auto' : rect.bottom - containerRect.top,
            bottom: openAbove ? containerRect.bottom - rect.top : 'auto',
            left: rect.left - containerRect.left,
            right: 'auto',
          });
        }
        frameId = requestAnimationFrame(syncPosition);
      };
      syncPosition();

      const handleCloseMenu = (e) => {
        if (
          !button.contains(e.target) &&
          !menuRef.current?.contains(e.target)
        ) {
          setShowMenu(false);
        }
      };
      document.addEventListener('click', handleCloseMenu, {
        capture: true,
      });
      return () => {
        document.removeEventListener('click', handleCloseMenu, {
          capture: true,
        });
        cancelAnimationFrame(frameId);
      };
    }
  }, [showMenu, requiredMenuHeight, setShowMenu]);

  const [dropping, setDropping] = useState(false);
  useEffect(() => {
    if (dropping && !placing) {
      setDropping(false);
      propsRef.current.onDrop?.();
    }
    if (!dropping && !placing) {
      onBlurTooltip();
    }
  }, [placing, dropping, onBlurTooltip]);

  if (placing && !disableRootAdds && !disableDropInLoop) {
    return (
      <PlacementWrapper centered={centered}>
        <PlacementButton
          dropping={dropping}
          onClick={onClick}
          {...others}
          {...tooltipProps}
          onBlur={onBlurTooltip}
        >
          {t('Place Here')}
          <DropArea
            onMouseEnter={() => setDropping(true)}
            onMouseLeave={() => setDropping(false)}
          />
          {tooltip}
        </PlacementButton>
        <Handle
          id="handle-left"
          type="target"
          position="left"
          style={{ left: 0 }}
        />
        <Handle
          id="handle-right"
          type="source"
          position="right"
          style={{ right: 0 }}
        />
      </PlacementWrapper>
    );
  }
  return (
    <ConsistentHeightWrapper>
      {!(disableFeatures || disableDropInLoop) ? (
        <>
          <StyledPlusButton
            title={t('Add Item')}
            ref={buttonRef}
            sizing="overlay"
            color={showMenu ? colorsButton.blue.hover : colorsButton.blue}
            onClick={(ev) => {
              setShowMenu(true);
              if (onClick) onClick(ev);
            }}
            {...others}
            data-qa="add-step-button"
          >
            <IconSizing size="16px" force={1}>
              {/* Effectively 12px wide due to icon's aspect ratio and padding */}
              <Icon icon="plus" />
            </IconSizing>
            <Handle
              id="handle-left"
              type="target"
              position="left"
              style={{ left: 0 }}
            />
            <Handle
              id="handle-right"
              type="source"
              position="right"
              style={{ right: 0 }}
            />
          </StyledPlusButton>
          {!showMenu || !menuPosition
            ? null
            : createPortal(
                <StyledMenu ref={menuRef} style={menuPosition}>
                  <Select
                    menuOnly
                    menuIsOpen
                    onChange={(val) => {
                      setShowMenu(false);
                      onSelectOption?.(val);
                    }}
                    options={options}
                    className="add-step-menu" // data-qa doesn't propogate
                  />
                </StyledMenu>,
                containerRef.current
              )}
        </>
      ) : (
        <StyledPlusButton
          ref={buttonRef}
          sizing="overlay"
          color={showMenu ? colorsButton.blue.hover : colorsButton.blue}
          {...others}
          data-qa="add-step-button"
        >
          <Handle
            id="handle-left"
            type="target"
            position="left"
            style={{ left: 0 }}
          />
          <Handle
            id="handle-right"
            type="source"
            position="right"
            style={{ right: 0 }}
          />
        </StyledPlusButton>
      )}
    </ConsistentHeightWrapper>
  );
}
